import React, { useMemo } from "react";
import { ResponsiveBar } from "@nivo/bar";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { formatRsePercentage, takeTopRseRows } from "../reporteSaludEmergenciaUtils";

const splitLabelInTwoLines = (value) => {
  const words = String(value || "").trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return words;

  const targetLength = Math.ceil(words.join(" ").length / 2);
  const firstLine = [];
  const secondLine = [];

  words.forEach((word) => {
    const nextLength = firstLine.join(" ").length + (firstLine.length ? 1 : 0) + word.length;
    if (nextLength <= targetLength || firstLine.length === 0) firstLine.push(word);
    else secondLine.push(word);
  });

  return [firstLine.join(" "), secondLine.join(" ")].filter(Boolean);
};

const WrappedAxisTick = ({ tick, label, fontSize, color }) => {
  const lines = splitLabelInTwoLines(label);
  const firstLineOffset = lines.length === 2 ? -6 : 0;

  return (
    <g transform={`translate(${tick.x},${tick.y})`} style={{ opacity: tick.opacity }}>
      <text textAnchor="end" dominantBaseline="central" fill={color} fontSize={fontSize}>
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={-8} dy={index === 0 ? firstLineOffset : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

const TopPatologiasChart = ({ data, loading, error, showProjectCode = true }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const sourceRows = data?.rows;
  const chartData = useMemo(() => {
    const rows = Array.isArray(sourceRows) ? sourceRows : [];
    const topRows = takeTopRseRows(rows, "atenciones", 10);

    return topRows.map((row, index) => {
      const name = row.patologia || "Sin dato";
      const projectCode = row.codigo || "";

      return {
        rankingId: `${row.projectId ?? "all"}|${row.pathologyId ?? index}|${index}`,
        label: [name, showProjectCode ? projectCode : ""].filter(Boolean).join(" · "),
        name,
        projectCode,
        atenciones: Number(row.atenciones),
        porcentaje: row.porcentajeAtencionesProyecto,
      };
    });
  }, [showProjectCode, sourceRows]);
  const labels = useMemo(
    () => new Map(chartData.map((row) => [row.rankingId, row.label])),
    [chartData]
  );
  const leftAxisMargin = isSmall ? 140 : isTablet ? 210 : isMedium ? 280 : 340;
  const tickFontSize = isSmall ? 8.5 : isTablet ? 9.5 : 10.5;

  return (
    <Paper
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        boxShadow: "0 8px 22px rgba(15, 23, 42, 0.06)",
        p: { xs: 2, md: 2.5 },
        height: "100%",
      }}
      data-testid="patologias-panel"
    >
      <Typography variant="h5" sx={{ fontWeight: 850 }}>
        Top 10 patologías
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        Patologías agregadas según las reglas epidemiológicas del backend.
      </Typography>

      {error ? (
        <Alert severity="error">No fue posible cargar esta información.</Alert>
      ) : loading ? (
        <Stack minHeight={420} alignItems="center" justifyContent="center" spacing={1.5}>
          <CircularProgress aria-label="Cargando patologías" />
          <Typography color="text.secondary">Cargando patologías...</Typography>
        </Stack>
      ) : chartData.length === 0 ? (
        <Box minHeight={420} display="flex" alignItems="center" justifyContent="center" textAlign="center">
          <Typography color="text.secondary">No hay datos disponibles para este período.</Typography>
        </Box>
      ) : (
        <>
          <Box height={440} minWidth={0}>
            <ResponsiveBar
              data={chartData}
              keys={["atenciones"]}
              indexBy="rankingId"
              layout="horizontal"
              margin={{ top: 8, right: 30, bottom: 42, left: leftAxisMargin }}
              padding={0.25}
              colors={["#009fe3"]}
              borderRadius={3}
              enableLabel={false}
              axisTop={null}
              axisRight={null}
              axisLeft={{
                tickSize: 0,
                tickPadding: 8,
                renderTick: (tick) => (
                  <WrappedAxisTick
                    tick={tick}
                    label={labels.get(tick.value) || tick.value}
                    fontSize={tickFontSize}
                    color={theme.palette.text.secondary}
                  />
                ),
              }}
              axisBottom={{ tickSize: 0, tickPadding: 8, tickValues: 5 }}
              theme={{
                axis: { ticks: { text: { fill: theme.palette.text.secondary } } },
                tooltip: { container: { color: theme.palette.text.primary } },
              }}
              tooltip={({ value, data: row }) => (
                <Paper sx={{ p: 1.25, borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Patología: {row.name}
                  </Typography>
                  {row.projectCode && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      Proyecto: {row.projectCode}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 800 }}>
                    Atenciones: {Number(value).toLocaleString("es-VE")}
                  </Typography>
                  {row.porcentaje !== null && row.porcentaje !== undefined && (
                    <Typography variant="caption">
                      Porcentaje: {formatRsePercentage(row.porcentaje)}
                    </Typography>
                  )}
                </Paper>
              )}
              role="application"
              ariaLabel="Top patologías"
            />
          </Box>
          <Typography variant="caption" color="text.secondary">
            Mostrando las 10 patologías con mayor número de atenciones. El Excel contiene el detalle completo.
          </Typography>
        </>
      )}
    </Paper>
  );
};

export default TopPatologiasChart;
